import {
  autoPlacement,
  autoUpdate,
  flip,
  offset,
  safePolygon,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useFloatingTree,
  useFocus,
  useHover,
  useInteractions,
  useListNavigation,
  useRole,
  useTypeahead,
} from "@floating-ui/react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useControlledValue } from "../../hooks/useControlledValue";
import type { DropdownContextValue, DropdownProps } from "./types";
import { pxToRem } from "../../utils";

export const useViewModel = (props: DropdownProps): DropdownContextValue => {
  const {
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
    defaultIsOpen,
    disabled = false,
    placement = "bottom",
    offset: offsetValue = 8,
    flip: flipEnabled = true,
    shift: shiftEnabled = true,
    autoPlacement: autoPlacementEnabled = false,
    interactions = ["click", "dismiss"],
    role = "dialog",
    minWidth,
    maxWidth,
    minHeight,
    maxHeight,
    matchTriggerWidth = false,
    typeaheadListRef,
    triggerId: propsTriggerId,
    contentId: propsContentId,
    middleware,
    clickOptions,
    hoverOptions,
    focusOptions,
    dismissOptions,
    roleOptions,
    listNavigationOptions,
    typeaheadOptions,
  } = props;

  const generatedId = useId();
  const generatedTriggerId = `${generatedId}-trigger`;
  const generatedContentId = `${generatedId}-content`;
  const triggerId = propsTriggerId || generatedTriggerId;
  const contentId = propsContentId || generatedContentId;

  const { value: open, onChange: onOpenChange } = useControlledValue<boolean>({
    controlledValue: controlledOpen,
    controlledOnChange: controlledOnOpenChange,
    defaultValue: defaultIsOpen,
  });

  const tree = useFloatingTree();

  const handleDismiss = useCallback(() => {
    onOpenChange(false);

    if (tree) {
      tree.events.emit("dismissAll");
    }
  }, [onOpenChange, tree]);

  useEffect(() => {
    if (!tree) {
      return;
    }

    const handleDismissAll = () => {
      onOpenChange(false);
    };

    tree.events.on("dismissAll", handleDismissAll);
    return () => {
      tree.events.off("dismissAll", handleDismissAll);
    };
  }, [tree, onOpenChange]);

  const listRef = useRef<(HTMLElement | null)[]>([]);
  const navigationRef = useCallback((index: number) => {
    return (node: HTMLElement | null): void => {
      listRef.current[index] = node;
    };
  }, []);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const {
    refs,
    floatingStyles,
    context,
    placement: currentPlacement,
  } = useFloating({
    open,
    onOpenChange,
    placement,
    middleware:
      middleware ??
      [
        offset(offsetValue),
        flipEnabled && flip(),
        shiftEnabled && shift(),
        autoPlacementEnabled && autoPlacement(),
        size({
          apply({ availableWidth, availableHeight, elements, rects }) {
            const finalMaxWidth = maxWidth
              ? Math.min(maxWidth, availableWidth)
              : availableWidth;
            const finalMaxHeight = maxHeight
              ? Math.min(maxHeight, availableHeight)
              : availableHeight;

            const width = matchTriggerWidth ? rects.reference.width : undefined;

            Object.assign(elements.floating.style, {
              maxWidth: `${finalMaxWidth}px`,
              maxHeight: `${finalMaxHeight}px`,
              ...(minWidth && { minWidth: pxToRem(minWidth) }),
              ...(minHeight && { minHeight: pxToRem(minHeight) }),
              ...(width && { width: `${width}px` }),
            });
          },
        }),
      ].filter(Boolean),
    whileElementsMounted: autoUpdate,
  });

  const isInteractionEnabled = (interaction: string) => {
    return (interactions as string[]).includes(interaction);
  };

  const click = useClick(context, {
    ...clickOptions,
    enabled:
      clickOptions?.enabled || (isInteractionEnabled("click") && !disabled),
    event: clickOptions?.event || "click",
    toggle: clickOptions?.toggle || true,
  });

  const hover = useHover(context, {
    ...hoverOptions,
    enabled:
      hoverOptions?.enabled || (isInteractionEnabled("hover") && !disabled),
    delay: hoverOptions?.delay,
    handleClose: hoverOptions?.handleClose || safePolygon(),
  });

  const focus = useFocus(context, {
    ...focusOptions,
    enabled:
      focusOptions?.enabled || (isInteractionEnabled("focus") && !disabled),
  });

  const dismiss = useDismiss(context, {
    ...dismissOptions,
    enabled:
      dismissOptions?.enabled || (isInteractionEnabled("dismiss") && !disabled),
    outsidePress:
      dismissOptions?.outsidePress ||
      ((event) => {
        const target = event.target as HTMLElement;
        const isOwnDropdown = refs.floating.current?.contains(target);
        // @ts-ignore
        const isTrigger = refs.reference.current?.contains?.(target);
        return !isOwnDropdown && !isTrigger;
      }),
    escapeKey: dismissOptions?.escapeKey || true,
    bubbles: dismissOptions?.bubbles || {
      escapeKey: false,
    },
  });

  const roleProps = useRole(context, {
    ...roleOptions,
    role: roleOptions?.role || role,
  });

  const listNavigation = useListNavigation(context, {
    ...listNavigationOptions,
    enabled:
      listNavigationOptions?.enabled ||
      (isInteractionEnabled("listNavigation") && !disabled),
    listRef: listNavigationOptions?.listRef || listRef,
    activeIndex: listNavigationOptions?.activeIndex || activeIndex,
    onNavigate: listNavigationOptions?.onNavigate || setActiveIndex,
    loop: listNavigationOptions?.loop || true,
    nested: listNavigationOptions?.nested,
    parentOrientation: listNavigationOptions?.parentOrientation,
    rtl: listNavigationOptions?.rtl,
  });

  const typeahead = useTypeahead(context, {
    ...typeaheadOptions,
    enabled:
      typeaheadOptions?.enabled ||
      (isInteractionEnabled("typeahead") && !disabled),
    listRef: typeaheadOptions?.listRef || typeaheadListRef || { current: [] },
    activeIndex: typeaheadOptions?.activeIndex || activeIndex,
    onMatch: typeaheadOptions?.onMatch || setActiveIndex,
    resetMs: typeaheadOptions?.resetMs || 750,
  });

  const interactionHooks = [
    click,
    hover,
    focus,
    dismiss,
    roleProps,
    listNavigation,
    typeahead,
  ];

  const { getReferenceProps, getFloatingProps, getItemProps } =
    useInteractions(interactionHooks);

  return {
    refs,
    floatingStyles,
    context,
    getReferenceProps,
    getFloatingProps,
    getItemProps: isInteractionEnabled("listNavigation")
      ? getItemProps
      : undefined,
    open: !!open,
    setOpen: onOpenChange,
    placement: currentPlacement,
    activeIndex,
    setActiveIndex,
    navigationRef,
    props,
    dismiss: handleDismiss,
    triggerId,
    contentId,
  };
};
